import React, { useState, useCallback, useEffect } from "react"
import { Handle, Position } from "reactflow"
import "./Node.css"
import "./RequestNode.css"
import { 
  Globe, 
  Minus,
  Plus,
  MoreHorizontal, 
  Play, 
  MoreHorizontal as MoreHorizontalIcon,
  Table2,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Loader,
  Trash2,
  Copy,
  Search,
  Eye,
  EyeOff,
} from "lucide-react"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import EnhancedVariableReference from "./EnhancedVariableReference"
import {
  previewDynamicDateText,
  DEFAULT_DYNAMIC_DATE_FORMAT,
  DYNAMIC_DATE_FORMAT_PRESETS,
  normalizeDynamicDateFormatPreset,
} from "@/lib/dateUtils"
import CopyPasteButtons from "./CopyPasteButtons"
import { useWorkflowGraphForVariableDiscovery } from "@/hooks/useWorkflowGraphForVariableDiscovery"

function normalizeHeadersObject(h) {
  if (!h || typeof h !== "object" || Array.isArray(h)) return {}
  const out = {}
  Object.entries(h).forEach(([k, v]) => {
    if (k != null && String(k).trim() !== "") out[String(k)] = v == null ? "" : String(v)
  })
  return out
}

/** Split saved headers into auth UI state + any other headers (legacy / future). */
function parseAuthFromHeaders(h) {
  const n = normalizeHeadersObject(h)
  const otherHeaders = {}
  let authType = "none"
  let bearerToken = ""

  Object.entries(n).forEach(([key, value]) => {
    if (key.toLowerCase() === "authorization") {
      const raw = String(value).trim()
      const m = raw.match(/^Bearer\s+(.+)$/i)
      bearerToken = m ? m[1].trim() : raw
      authType = bearerToken ? "bearer" : "none"
    } else {
      otherHeaders[key] = value
    }
  })

  return { authType, bearerToken, otherHeaders }
}

/** Build persisted headers object from auth UI (Authorization + optional other keys). */
function buildHeadersFromAuth(authType, bearerToken, otherHeaders) {
  const o = { ...otherHeaders }
  Object.keys(o).forEach((k) => {
    if (k.toLowerCase() === "authorization") delete o[k]
  })
  if (authType === "bearer" && bearerToken.trim()) {
    o.Authorization = `Bearer ${bearerToken.trim()}`
  }
  return o
}

const RequestNode = ({ data, id, csrf_token }) => {
  const { currentWorkflow, workflows, deleteNode, updateNode } = useWorkflow()
  const { nodes, edges } = useWorkflowGraphForVariableDiscovery(currentWorkflow?.id)
  const { expanded = true } = data
  const [response, setResponse] = useState(data.response || null)
  const [responsePreview, setResponsePreview] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showFullResponse, setShowFullResponse] = useState(false)
  const [extractedVariables, setExtractedVariables] = useState(data.extractedVariables || [])
  const [activeTab, setActiveTab] = useState("request")
  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [showSquareTooltip, setShowSquareTooltip] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  const [responseCopySuccess, setResponseCopySuccess] = useState(false)
  const [variableSearchTerm, setVariableSearchTerm] = useState("")

  const initialAuth = parseAuthFromHeaders(data.headers)

  const [authType, setAuthType] = useState(initialAuth.authType)
  const [bearerToken, setBearerToken] = useState(initialAuth.bearerToken)
  const [otherHeaders, setOtherHeaders] = useState(initialAuth.otherHeaders)
  const [showBearerToken, setShowBearerToken] = useState(false)

  const [requestData, setRequestData] = useState({
    name: data.name || "HTTP Request",
    description: data.description || "",
    url: data.url || "",
    method: data.method || "GET",
    body: data.body || "",
    dynamicDateFormat: data.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
    headers: buildHeadersFromAuth(
      initialAuth.authType,
      initialAuth.bearerToken,
      initialAuth.otherHeaders,
    ),
  })

  useEffect(() => {
    const p = parseAuthFromHeaders(data.headers)
    setAuthType(p.authType)
    setBearerToken(p.bearerToken)
    setOtherHeaders(p.otherHeaders)
    setRequestData((prev) => ({
      ...prev,
      dynamicDateFormat: data.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      headers: buildHeadersFromAuth(p.authType, p.bearerToken, p.otherHeaders),
    }))
  }, [id])

  useEffect(() => {
    setRequestData((prev) => ({
      ...prev,
      dynamicDateFormat: data.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
    }))
  }, [data.dynamicDateFormat])

  // Add effect to handle panning prevention
  useEffect(() => {
    // Function to prevent mouse events that would cause panning
    const preventPanning = (e) => {
      if (document.body.classList.contains("disable-flow-panning")) {
        // Only prevent default on middle mouse button (wheel) or when dragging with left button
        if (
          e.button === 1 ||
          (e.button === 0 && e.type === "mousemove" && e.buttons === 1)
        ) {
          e.stopPropagation();
        }
      }
    };

    // Add event listeners to the document
    document.addEventListener("mousedown", preventPanning, {
      capture: true,
      passive: false,
    });
    document.addEventListener("mousemove", preventPanning, {
      capture: true,
      passive: false,
    });

    // Clean up event listeners on component unmount
    return () => {
      document.removeEventListener("mousedown", preventPanning, {
        capture: true,
        passive: false,
      });
      document.removeEventListener("mousemove", preventPanning, {
        capture: true,
        passive: false,
      });
    };
  }, []);

  // Add effect to handle scroll containment within the node
  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest('.node-content')
      
      if (nodeContent) {
        // Check if Ctrl key is pressed (for zoom functionality)
        if (e.ctrlKey) {
          // Allow default behavior for zoom
          return
        }

        // Check if Shift key is pressed (for global horizontal scrolling)
        if (e.shiftKey) {
          // Allow default behavior for global horizontal scrolling
          return
        }

        // Check if the node-content is scrollable and has scrollable content
        const isScrollable = nodeContent.scrollHeight > nodeContent.clientHeight
        const isAtTop = nodeContent.scrollTop === 0
        const isAtBottom = nodeContent.scrollTop + nodeContent.clientHeight >= nodeContent.scrollHeight - 1

        // Only stop propagation if we're scrolling within scrollable bounds
        if (isScrollable && !(isAtTop && e.deltaY < 0) && !(isAtBottom && e.deltaY > 0)) {
          e.stopPropagation()
        }
      }
    }

    // Add wheel event listener to capture scroll events
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true })

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true })
    }
  }, [id, expanded]);

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  const extractVariablesFromResponse = useCallback((responseData) => {
    const variables = []
    
    if (Array.isArray(responseData)) {
      // Handle array response - extract from first item
      if (responseData.length > 0) {
        const firstItem = responseData[0]
        Object.keys(firstItem).forEach(key => {
          variables.push({
            name: key,
            value: firstItem[key],
            type: typeof firstItem[key],
            isArray: true
          })
        })
      }
    } else if (typeof responseData === 'object' && responseData !== null) {
      // Handle single object response
      Object.keys(responseData).forEach(key => {
        variables.push({
          name: key,
          value: responseData[key],
          type: typeof responseData[key],
          isArray: false
        })
      })
    }
    
    return variables
  }, [])

  const testUrl = async () => {
    if (!requestData.url.trim()) {
      setError("Please enter a URL")
      return
    }

    if (!currentWorkflow?.id) {
      setError("Workflow not loaded — save or open a workflow first")
      return
    }

    setIsLoading(true)
    setError(null)
    setResponse(null)
    setResponsePreview("")

    try {
      const resolvedUrl = requestData.url.includes("~")
        ? previewDynamicDateText(requestData.url, new Date(), requestData.dynamicDateFormat)
        : requestData.url

      const resolvedBody =
        requestData.body && requestData.body.includes("~")
          ? previewDynamicDateText(requestData.body, new Date(), requestData.dynamicDateFormat)
          : requestData.body

      const requestHeaders = {
        ...buildHeadersFromAuth(authType, bearerToken, otherHeaders),
      }
      const hasContentType = Object.keys(requestHeaders).some(
        (k) => k.toLowerCase() === "content-type",
      )
      if (
        requestData.method !== "GET" &&
        resolvedBody &&
        String(resolvedBody).trim() &&
        !hasContentType
      ) {
        requestHeaders["Content-Type"] = "application/json"
      }

      const payloadBody =
        requestData.method !== "GET" && resolvedBody && String(resolvedBody).trim()
          ? resolvedBody
          : null

      const response = await fetch(
        `/workflows/${currentWorkflow.id}/test-http-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": csrf_token,
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: JSON.stringify({
            url: resolvedUrl,
            method: requestData.method,
            headers: requestHeaders,
            body: payloadBody,
            dynamicDateFormat: requestData.dynamicDateFormat,
          }),
        },
      )

      const responseText = await response.text()
      let parsed
      try {
        parsed = JSON.parse(responseText)
      } catch {
        setError(responseText || "Invalid response from server")
        return
      }

      if (!parsed.success) {
        setError(parsed.message || "Request failed")
        return
      }

      const responseData = parsed.data
      const raw = parsed.raw != null ? String(parsed.raw) : responseText

      setResponse({
        status: parsed.httpStatus ?? response.status,
        statusText: response.statusText,
        data: responseData,
        raw,
      })

      const preview =
        typeof responseData === "object" && responseData !== null
          ? JSON.stringify(responseData, null, 2)
          : String(raw)

      setResponsePreview(preview.length > 200 ? preview.substring(0, 200) + "..." : preview)

      const variables = extractVariablesFromResponse(responseData)
      setExtractedVariables(variables)

      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, {
          ...data,
          url: requestData.url,
          method: requestData.method,
          body: requestData.body,
          headers: buildHeadersFromAuth(authType, bearerToken, otherHeaders),
          response: {
            status: parsed.httpStatus ?? response.status,
            statusText: response.statusText,
            data: responseData,
            raw,
          },
          extractedVariables: variables,
        })
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const getVariablePreview = (variable) => {
    if (!variable.value) {
      return "No value"
    }
    
    if (variable.type === 'string') {
      return variable.value.length > 50 ? variable.value.substring(0, 50) + "..." : variable.value
    }
    return JSON.stringify(variable.value)
  }

  // Get available variables from all connected request nodes throughout the workflow
  const getAvailableVariables = () => {
    if (!nodes || !edges) return []
    
    const availableVariables = []
    const visitedNodes = new Set()
    
    // Recursive function to find all upstream request nodes
    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)
      
      const node = nodes.find(n => n.id === nodeId)
      if (!node) return
      
      // If this is a request node, add its extracted variables
      if (node.type === 'requestNode' && node.data.extractedVariables) {
        availableVariables.push(...node.data.extractedVariables)
      }
      
      // Find all incoming edges to this node
      const incomingEdges = edges.filter(edge => edge.target === nodeId)
      
      // Recursively check all source nodes
      incomingEdges.forEach(edge => {
        findUpstreamRequestNodes(edge.source)
      })
    }
    
    // Start from the current request node
    findUpstreamRequestNodes(id)
    
    return availableVariables
  }

  const handleChange = (field, value) => {
    setRequestData((prev) => {
      const updatedData = { ...prev, [field]: value }
      if (currentWorkflow) {
        updateNode(currentWorkflow.id, id, { data: updatedData })
      }
      return updatedData
    })
  }

  const handleDateFormatChange = (format) => {
    setRequestData((prev) => {
      const updatedData = { ...prev, dynamicDateFormat: format }
      if (currentWorkflow) {
        updateNode(currentWorkflow.id, id, { data: updatedData })
      }
      return updatedData
    })
  }

  const syncHeadersToRequestData = (nextAuthType, nextToken, nextOther) => {
    const headers = buildHeadersFromAuth(nextAuthType, nextToken, nextOther)
    setRequestData((prev) => {
      const updatedData = { ...prev, headers }
      if (currentWorkflow) {
        updateNode(currentWorkflow.id, id, { data: updatedData })
      }
      return updatedData
    })
  }

  const updateAuthType = (value) => {
    setAuthType(value)
    syncHeadersToRequestData(value, bearerToken, otherHeaders)
  }

  const updateBearerToken = (value) => {
    setBearerToken(value)
    syncHeadersToRequestData(authType, value, otherHeaders)
  }

  const handleBlur = () => {
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, {
        ...requestData,
        headers: buildHeadersFromAuth(authType, bearerToken, otherHeaders),
      })
    }
  }

  // Copy request preview to clipboard
  const copyRequestPreview = async () => {
    try {
      const resolvedUrl = requestData.url.includes('~') ? 
        previewDynamicDateText(requestData.url, new Date(), requestData.dynamicDateFormat) : 
        requestData.url
      
      const hdrs = buildHeadersFromAuth(authType, bearerToken, otherHeaders)
      const headerBlock = Object.entries(hdrs).length
        ? Object.entries(hdrs)
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n")
        : ""

      let requestText
      if (requestData.method !== "GET" && requestData.body) {
        const resolvedBody = requestData.body.includes("~")
          ? previewDynamicDateText(requestData.body, new Date(), requestData.dynamicDateFormat)
          : requestData.body
        requestText = headerBlock
          ? `${requestData.method} ${resolvedUrl}\n\n${headerBlock}\n\n${resolvedBody}`
          : `${requestData.method} ${resolvedUrl}\n\n${resolvedBody}`
      } else {
        requestText = headerBlock ? `${resolvedUrl}\n\n${headerBlock}` : resolvedUrl
      }
      
      await navigator.clipboard.writeText(requestText)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy request preview:', err)
    }
  }

  // Copy response to clipboard
  const copyResponse = async () => {
    try {
      await navigator.clipboard.writeText(response.raw)
      setResponseCopySuccess(true)
      setTimeout(() => setResponseCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy response:', err)
    }
  }

  // Filter variables based on search term
  const filteredVariables = React.useMemo(() => {
    if (!variableSearchTerm.trim()) {
      return extractedVariables
    }
    
    const searchLower = variableSearchTerm.toLowerCase()
    return extractedVariables.filter(variable => {
      const nameMatch = variable.name.toLowerCase().includes(searchLower)
      const valueMatch = String(variable.value || '').toLowerCase().includes(searchLower)
      return nameMatch || valueMatch
    })
  }, [extractedVariables, variableSearchTerm])

  return (
    <div
      className={`trigger-node-container request-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? " request-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Node Header - This part should be draggable */}
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Globe size={16} className="trigger-icon" />
          <span>{requestData.name && requestData.name.trim() !== "" ? requestData.name : "HTTP Request"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons 
            nodeId={id}
            nodeType="requestNode"
            nodeName={requestData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          {/* <div className="square-button-container nodrag">
            <button
              className="trigger-node-button nodrag"
              onMouseEnter={() => setShowSquareTooltip(true)}
              onMouseLeave={() => setShowSquareTooltip(false)}
            >
              <Table2 size={16} />
            </button>
            {showSquareTooltip && <div className="square-tooltip">Open in tab</div>}
          </div> */}
          <button 
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm('Are you sure you want to delete this node?')) {
                try {
                  // Use the workflow context's deleteNode function which handles optimistic updates
                  if (deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, csrf_token)
                  } else {
                    throw new Error('Unable to delete node: missing deleteNode function or currentWorkflow')
                  }
                } catch (error) {
                  console.error('Failed to delete node:', error)
                  alert('Failed to delete node. Please try again.')
                }
              }
            }}
            title="Delete node"
            style={{ color: '#ef4444' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Node Content - Should not be draggable and only visible when not minimized */}
      {expanded && (
        <div className="node-content">
          {/* Tabs */}
          <div className="request-tabs dark:border-border">
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "request" ? "active" : ""}`}
              onClick={() => setActiveTab("request")}
            >
              Request
            </button>
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "response" ? "active" : ""}`}
              onClick={() => setActiveTab("response")}
            >
              Response
            </button>
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "variables" ? "active" : ""}`}
              onClick={() => setActiveTab("variables")}
            >
              Variables
            </button>
          </div>

          {/* Request Tab */}
          {activeTab === "request" && (
            <div className="request-tab-content">
              <div className="node-field">
                <label className="dark:text-white">Request Name</label>
                <EnhancedVariableReference
                  value={requestData.name}
                  onChange={(value) => handleChange("name", value)}
                  onBlur={() => handleBlur()}
                  availableVariables={getAvailableVariables()}
                  placeholder="Enter request name or use variables..."
                  className="w-full"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={requestData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Description</label>
                <EnhancedVariableReference
                  value={requestData.description}
                  onChange={(value) => handleChange("description", value)}
                  onBlur={() => handleBlur()}
                  availableVariables={getAvailableVariables()}
                  placeholder="Describe what this request does or use variables..."
                  className="w-full"
                  multiline={true}
                  rows={2}
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={requestData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              <div
                className="node-field nodrag"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <label className="dark:text-white text-xs font-medium block mb-1">
                  Date output format
                </label>
                <select
                  value={normalizeDynamicDateFormatPreset(requestData.dynamicDateFormat)}
                  onChange={(e) => handleDateFormatChange(e.target.value)}
                  className="method-select w-full dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none nodrag focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  {DYNAMIC_DATE_FORMAT_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                  Used for ~date~ tokens in URL, body, previews, and when the workflow runs.
                </p>
              </div>

              <div className="node-field">
                <label className="dark:text-white">URL</label>
                <EnhancedVariableReference
                  value={requestData.url}
                  onChange={(value) => handleChange("url", value)}
                  onBlur={() => handleBlur()}
                  availableVariables={getAvailableVariables()}
                  placeholder="https://api.example.com/endpoint or use variables..."
                  className="url-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                  showDateVariables={true}
                  showPreview={false}
                  dateFormat={requestData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              {/* Request Preview Section */}
              {requestData.url && (
                <div className="node-field">
                  <div className="request-preview-header-with-actions">
                    <label className="dark:text-white">Request Preview</label>
                    <div className="request-preview-actions">
                      <button 
                        className={`test-button ${isLoading ? 'loading' : ''}`}
                        onClick={testUrl} 
                        disabled={isLoading}
                        title="Test this request"
                      >
                        {isLoading ? <Loader size={14} className="spinner" /> : "Test"}
                      </button>
                      <button 
                        className={`copy-button ${copySuccess ? 'success' : ''}`}
                        onClick={copyRequestPreview}
                        title="Copy request to clipboard"
                      >
                        {copySuccess ? (
                          <>
                            <CheckCircle size={14} />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy size={14} />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="request-preview-container">
                    <div className="request-url-preview">
                      {requestData.url.includes('~') ? 
                        previewDynamicDateText(requestData.url, new Date(), requestData.dynamicDateFormat) : 
                        requestData.url
                      }
                    </div>
                    {requestData.method !== 'GET' && requestData.body && (
                      <div className="request-body-preview">
                        <div className="request-body-label">Body:</div>
                        <pre className="request-body-content">
                          {requestData.body.includes('~') ? 
                            previewDynamicDateText(requestData.body, new Date(), requestData.dynamicDateFormat) : 
                            requestData.body
                          }
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="node-field">
                <label className="dark:text-white">Method</label>
                <select 
                  value={requestData.method} 
                  onChange={(e) => handleChange("method", e.target.value)} 
                  onBlur={() => handleBlur()}
                  className="method-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <div className="node-field nodrag space-y-2">
                <label className="dark:text-white">Auth Type</label>
                <select
                  value={authType}
                  onChange={(e) => updateAuthType(e.target.value)}
                  onBlur={() => handleBlur()}
                  className="method-select w-full dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                >
                  <option value="none">No Auth</option>
                  <option value="bearer">Bearer Token</option>
                </select>
                {authType === "bearer" && (
                  <div className="space-y-2 pt-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                      The{" "}
                      <code className="text-gray-300">Authorization</code> header
                      is sent as{" "}
                      <code className="text-gray-300">Bearer &lt;token&gt;</code>.
                    </p>
                    <label className="dark:text-white text-sm block">Token</label>
                    <div className="flex gap-1 items-stretch">
                      <input
                        type={showBearerToken ? "text" : "password"}
                        autoComplete="off"
                        spellCheck={false}
                        className="flex-1 min-w-0 text-sm px-2 py-1.5 rounded border border-gray-600 bg-[#1f2937] text-white nodrag"
                        placeholder="Paste your API token"
                        value={bearerToken}
                        onChange={(e) => updateBearerToken(e.target.value)}
                        onBlur={() => handleBlur()}
                      />
                      <button
                        type="button"
                        className="nodrag shrink-0 px-2 rounded border border-gray-600 bg-[#1f2937] text-gray-300 hover:text-white hover:bg-[#374151]"
                        title={showBearerToken ? "Hide token" : "Show token"}
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowBearerToken((v) => !v)
                        }}
                      >
                        {showBearerToken ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {requestData.method !== 'GET' && (
                <div className="node-field">
                  <label className="dark:text-white">Request Body</label>
                  <EnhancedVariableReference
                    value={requestData.body}
                    onChange={(value) => handleChange("body", value)}
                    onBlur={() => handleBlur()}
                    availableVariables={getAvailableVariables()}
                    placeholder="Enter JSON body or use variables..."
                    className="body-textarea"
                    multiline={true}
                    rows={4}
                    showDateVariables={true}
                    showPreview={false}
                    dateFormat={requestData.dynamicDateFormat}
                    onDateFormatChange={handleDateFormatChange}
                  />
                </div>
              )}

              {/* Response Summary */}
              {error && (
                <div className="error-message">
                  <XCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {response && (
                <div className="response-summary dark:bg-[#1f2937] dark:border-none dark:focus:outline-none">
                  <div className="response-status">
                    <span className={`status-badge ${response.status < 400 ? 'success' : 'error'}`}>
                      {response.status} {response.statusText}
                    </span>
                  </div>
                  <div className="response-summary-text">
                    {response.status < 400 ? (
                      <span className="success-text">
                        <CheckCircle size={14} />
                        Request successful
                      </span>
                    ) : (
                      <span className="error-text">
                        <XCircle size={14} />
                        Request failed
                      </span>
                    )}
                  </div>
                </div>
              )}

              {!response && !error && (
                <div className="no-response">
                  <ExternalLink size={24} />
                  <p>Click "Test" to make a request and see the response</p>
                </div>
              )}
            </div>
          )}

          {/* Response Tab */}
          {activeTab === "response" && (
            <div className="response-tab-content">
              {error && (
                <div className="error-message">
                  <XCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {response && (
                <div className="response-info dark:bg-[#1f2937] dark:border-none dark:focus:outline-none">
                  <div className="response-status">
                    <span className={`status-badge ${response.status < 400 ? 'success' : 'error'}`}>
                      {response.status} {response.statusText}
                    </span>
                  </div>
                  
                  <div className="response-preview">
                    <div className="response-header-with-copy">
                      <span className="response-title dark:text-white">Response Data</span>
                      <button 
                        className={`copy-button ${responseCopySuccess ? 'success' : ''}`}
                        onClick={copyResponse}
                        title="Copy response to clipboard"
                      >
                        {responseCopySuccess ? (
                          <>
                            <CheckCircle size={14} />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy size={14} />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="response-content-container">
                      <pre className="response-content dark:bg-[#1f2937] dark:border-none dark:focus:outline-none dark:text-white">
                        {response.raw}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {!response && !error && (
                <div className="no-response">
                  <ExternalLink size={24} />
                  <p>Click "Test" to make a request and see the response</p>
                </div>
              )}
            </div>
          )}

          {/* Variables Tab */}
          {activeTab === "variables" && (
            <div className="variables-tab-content">
              {extractedVariables.length > 0 ? (
                <div className="variables-list-scrollable">
                  <div className="variables-header">
                    <span>Available Variables</span>
                    <span className="variable-count dark:bg-[#1f2937] dark:text-white">{filteredVariables.length} of {extractedVariables.length} variables</span>
                  </div>
                  
                  {/* Search Input */}
                  <div className="variables-search-container">
                    <div className="variables-search-input-wrapper">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search variables by name or value..."
                        value={variableSearchTerm}
                        onChange={(e) => setVariableSearchTerm(e.target.value)}
                        className="variables-search-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div className="variables-items dark:bg-[#1f2937] dark:border-none dark:focus:outline-none dark:text-white">
                    {filteredVariables.length > 0 ? (
                      filteredVariables.map((variable, index) => (
                        <div key={index} className="variable-item dark:bg-card dark:border-none dark:focus:outline-none">
                          <div className="variable-info">
                            <span className="variable-name dark:text-white">{variable.name}</span>
                            <span className="variable-type dark:bg-[#1f2937] dark:text-white">{variable.type}</span>
                            {variable.isArray && <span className="array-badge">Array</span>}
                          </div>
                          <div className="variable-preview dark:bg-[#1f2937] dark:border-none dark:focus:outline-none dark:text-white">
                            {getVariablePreview(variable)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-variables-found">
                        <Search size={24} />
                        <p>No variables found matching "{variableSearchTerm}"</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-variables">
                  <CheckCircle size={24} />
                  <p>Variables will appear here after testing the request</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Input handle - dynamically positioned based on handlePosition data */}
      <Handle
        type="target"
        // position={data.handlePosition === 'top-bottom' ? Position.Top : Position.Left}
        position={Position.Top}
        id="b"
      />

      {/* Output handle - dynamically positioned based on handlePosition data */}
      <Handle
        type="source"
        // position={data.handlePosition === 'top-bottom' ? Position.Bottom : Position.Right}
        position={Position.Bottom}
        id="a"
      />
    </div>
  )
}

export default RequestNode 